(() => {
  'use strict';

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function escapeHTML(value = '') {
    return String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);
  }

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (Number.isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format = '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${formatWithDelimiters(Number(cents))}`;

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(Number(cents), 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(Number(cents), 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(Number(cents), 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(Number(cents), 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(Number(cents), 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(Number(cents), 2);
    }
    return format.replace(match[0], value);
  }

  const money = (cents) => formatMoney(cents, window.MOVR?.moneyFormat || '${{amount}}');

  function initMenu() {
    const toggle = qs('[data-menu-toggle]');
    const menu = qs('[data-mobile-menu]');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      const opening = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(opening));
      menu.classList.toggle('is-open', opening);
      qs('.visually-hidden', toggle).textContent = opening ? 'Close menu' : 'Open menu';
    });

    qsa('a', menu).forEach((link) => {
      link.addEventListener('click', () => {
        toggle.setAttribute('aria-expanded', 'false');
        menu.classList.remove('is-open');
        qs('.visually-hidden', toggle).textContent = 'Open menu';
      });
    });
  }

  const cartDrawer = qs('[data-cart-drawer]');
  const cartOverlay = qs('[data-cart-overlay]');
  const cartItems = qs('[data-cart-items]');
  const cartFooter = qs('[data-cart-footer]');
  const cartError = qs('[data-cart-error]');
  let cartTrigger = null;

  function showCartError(message = '') {
    if (!cartError) return;
    cartError.textContent = message;
    cartError.hidden = !message;
  }

  function updateCartCounts(count) {
    qsa('[data-cart-count]').forEach((element) => {
      element.textContent = count;
    });
  }

  function cartItemMarkup(item, line) {
    const image = item.image
      ? `<img src="${escapeHTML(item.image)}&width=180" alt="${escapeHTML(item.product_title)}" width="86" height="96" loading="lazy">`
      : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p>${escapeHTML(item.variant_title)}</p>`
      : '';
    const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
      ? `<p>${escapeHTML(item.selling_plan_allocation.selling_plan.name)}</p>`
      : '';

    return `
      <article class="drawer-item" data-line="${line}">
        ${image}
        <div>
          <a href="${escapeHTML(item.url)}"><strong>${escapeHTML(item.product_title)}</strong></a>
          ${variant}
          ${sellingPlan}
          <div class="drawer-item__actions">
            <label>
              <span class="visually-hidden">Quantity for ${escapeHTML(item.product_title)}</span>
              <input type="number" min="0" value="${item.quantity}" data-cart-quantity>
            </label>
            <button type="button" data-cart-remove>Remove</button>
          </div>
        </div>
        <strong>${money(item.final_line_price)}</strong>
      </article>
    `;
  }

  function renderCart(cart) {
    if (!cartItems) return;
    updateCartCounts(cart.item_count);
    showCartError('');

    if (!cart.item_count) {
      cartItems.innerHTML = `
        <div class="empty-cart">
          <p>Nothing here yet.</p>
          <a class="button button--ink" href="/collections/all">Shop MOVR</a>
        </div>
      `;
      if (cartFooter) cartFooter.hidden = true;
      return;
    }

    cartItems.innerHTML = cart.items.map((item, index) => cartItemMarkup(item, index + 1)).join('');
    if (cartFooter) {
      cartFooter.hidden = false;
      const total = qs('[data-cart-total]', cartFooter);
      if (total) total.textContent = money(cart.total_price);
    }
  }

  async function getCart() {
    const response = await fetch(`${window.MOVR.routes.cart}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load your bag.');
    return response.json();
  }

  async function refreshCart() {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  }

  function revealCart() {
    if (!cartDrawer || !cartOverlay) return;
    document.body.classList.add('drawer-open');
    cartOverlay.hidden = false;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => qs('[data-cart-close]', cartDrawer)?.focus());
  }

  async function openCart(event) {
    cartTrigger = event?.currentTarget || document.activeElement;
    showCartError('');
    try {
      await refreshCart();
    } catch (error) {
      showCartError(error.message);
    }
    revealCart();
  }

  function closeCart() {
    if (!cartDrawer || !cartOverlay) return;
    document.body.classList.remove('drawer-open');
    cartOverlay.hidden = true;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (cartTrigger instanceof HTMLElement) cartTrigger.focus();
  }

  async function changeCartLine(line, quantity) {
    showCartError('');
    const response = await fetch(`${window.MOVR.routes.cartChange}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to update your bag.');
    renderCart(payload);
  }

  function initCart() {
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', openCart));
    qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    cartOverlay?.addEventListener('click', closeCart);

    cartItems?.addEventListener('click', async (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      const item = remove.closest('[data-line]');
      if (!item) return;
      remove.disabled = true;
      try {
        await changeCartLine(Number(item.dataset.line), 0);
      } catch (error) {
        showCartError(error.message);
        remove.disabled = false;
      }
    });

    cartItems?.addEventListener('change', async (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      const item = input.closest('[data-line]');
      if (!item) return;
      input.disabled = true;
      try {
        await changeCartLine(Number(item.dataset.line), Math.max(0, Number(input.value) || 0));
      } catch (error) {
        showCartError(error.message);
        input.disabled = false;
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && cartDrawer?.classList.contains('is-open')) closeCart();
    });
  }

  function initProductForms() {
    qsa('[data-product-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = qs('[type="submit"]', form);
        const status = qs('[data-product-status]', form);
        if (button) {
          button.disabled = true;
          button.classList.add('is-loading');
        }
        if (status) status.textContent = '';

        try {
          const response = await fetch(`${window.MOVR.routes.cartAdd}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const payload = await response.json();
          if (!response.ok) {
            throw new Error(payload.description || payload.message || 'Unable to add this item.');
          }
          if (status) status.textContent = 'Added to your bag.';
          await refreshCart();
          revealCart();
        } catch (error) {
          if (status) status.textContent = error.message;
        } finally {
          if (button) {
            button.disabled = false;
            button.classList.remove('is-loading');
          }
        }
      });
    });
  }

  function initProductRoot(root) {
    const productJSON = qs('[data-product-json]', root);
    const form = qs('.product-form', root);
    if (!productJSON || !form) return;

    let variants = [];
    try {
      variants = JSON.parse(productJSON.textContent);
    } catch (error) {
      return;
    }

    const variantInput = qs('[data-variant-id]', form);
    const addButton = qs('[data-add-to-cart]', form);
    const addText = qs('[data-add-text]', form);
    const mainImage = qs('.product-stage__image', root);
    const quantityInput = qs('[data-quantity-input]', form);
    let activeVariant = variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants[0];

    function selectedOptions() {
      return qsa('[data-option-index]', form).map((fieldset) => {
        return qs('input:checked', fieldset)?.value;
      });
    }

    function selectedPurchaseMode() {
      return qs('[data-purchase-mode]:checked', form)?.value || 'one_time';
    }

    function updatePricing() {
      if (!activeVariant) {
        if (addButton) addButton.disabled = true;
        if (addText) addText.textContent = 'Unavailable';
        return;
      }

      const quantity = Math.max(1, Number(quantityInput?.value) || 1);
      const allocation = activeVariant.selling_plan_allocations?.[0];
      const subscriptionUnitPrice = allocation?.price || activeVariant.price;
      const unitPrice = selectedPurchaseMode() === 'subscription' ? subscriptionUnitPrice : activeVariant.price;

      if (addButton) addButton.disabled = !activeVariant.available;
      if (addText) {
        const action = root.classList.contains('mockup-home') ? 'Add to cart' : 'Add to bag';
        addText.textContent = activeVariant.available ? `${action} · ${money(unitPrice * quantity)}` : 'Sold out';
      }
      qsa('[data-variant-price]', root).forEach((element) => {
        element.textContent = money(activeVariant.price);
      });
      qsa('[data-sticky-price]', root).forEach((element) => {
        element.textContent = money(unitPrice);
      });
      const subscriptionPrice = qs('[data-subscription-price]', root);
      if (subscriptionPrice) subscriptionPrice.textContent = money(subscriptionUnitPrice);
      const servingCount = qs('[data-serving-count]', root);
      if (servingCount) servingCount.textContent = `${quantity * 30} servings`;
      const servingPrice = qs('[data-serving-price]', root);
      if (servingPrice) servingPrice.textContent = `${money(Math.round(unitPrice / 30))} per serving`;
    }

    function setVariant(variant) {
      if (!variant) {
        activeVariant = null;
        updatePricing();
        return;
      }

      activeVariant = variant;
      if (variantInput) variantInput.value = variant.id;
      updatePricing();

      const variantImage = variant.featured_media?.preview_image?.src || variant.featured_image?.src || variant.featured_image;
      if (mainImage && variantImage) {
        mainImage.src = variantImage;
        mainImage.removeAttribute('srcset');
      }

      const nextURL = new URL(window.location.href);
      nextURL.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', nextURL.toString());
    }

    qsa('[data-option-index] input', form).forEach((input) => {
      input.addEventListener('change', () => {
        const choices = selectedOptions();
        const variant = variants.find((candidate) => {
          return candidate.options.every((option, index) => option === choices[index]);
        });
        setVariant(variant);
      });
    });

    qsa('[data-purchase-mode]', form).forEach((input) => {
      input.addEventListener('change', () => {
        qsa('.purchase-card', form).forEach((card) => card.classList.remove('is-selected'));
        input.closest('.purchase-card')?.classList.add('is-selected');
        const sellingPlan = qs('[data-selling-plan]', form);
        if (sellingPlan) sellingPlan.disabled = input.value !== 'subscription';
        updatePricing();
      });
    });

    qs('[data-quantity-minus]', form)?.addEventListener('click', () => {
      quantityInput.value = Math.max(1, (Number(quantityInput.value) || 1) - 1);
      updatePricing();
    });
    qs('[data-quantity-plus]', form)?.addEventListener('click', () => {
      quantityInput.value = Math.max(1, (Number(quantityInput.value) || 1) + 1);
      updatePricing();
    });
    quantityInput?.addEventListener('change', () => {
      quantityInput.value = Math.max(1, Number(quantityInput.value) || 1);
      updatePricing();
    });

    qsa('[data-pack-quantity]', form).forEach((input) => {
      input.addEventListener('change', () => {
        if (quantityInput) quantityInput.value = Math.max(1, Number(input.value) || 1);
        updatePricing();
      });
    });

    qsa('[data-gallery-thumb]', root).forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        qsa('[data-gallery-thumb]', root).forEach((item) => item.classList.remove('is-active'));
        thumbnail.classList.add('is-active');
        if (mainImage) {
          mainImage.src = thumbnail.dataset.mediaSrc;
          mainImage.alt = thumbnail.dataset.mediaAlt || '';
          mainImage.removeAttribute('srcset');
        }
      });
    });

    qsa('[data-scroll-to-buy]', root).forEach((button) => {
      button.addEventListener('click', () => {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    const stickyBuy = qs('[data-sticky-buy]', root);
    if (stickyBuy && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        stickyBuy.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0.1 });
      observer.observe(form);
    }

    const initialMode = qs('[data-purchase-mode]:checked', form);
    if (initialMode) {
      qsa('.purchase-card', form).forEach((card) => card.classList.remove('is-selected'));
      initialMode.closest('.purchase-card')?.classList.add('is-selected');
      const sellingPlan = qs('[data-selling-plan]', form);
      if (sellingPlan) sellingPlan.disabled = initialMode.value !== 'subscription';
    }
    updatePricing();
  }

  function initDialogs() {
    qsa('[data-dialog-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.dialogOpen);
        if (dialog?.showModal) dialog.showModal();
      });
    });

    qsa('[data-dialog]').forEach((dialog) => {
      qs('[data-dialog-close]', dialog)?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        const bounds = dialog.getBoundingClientRect();
        const inside = event.clientX >= bounds.left && event.clientX <= bounds.right
          && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
        if (!inside) dialog.close();
      });
    });
  }

  function init() {
    initMenu();
    initCart();
    initProductForms();
    qsa('[data-product-root]').forEach(initProductRoot);
    initDialogs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
